/**
 * Safe wrapper for localStorage access.
 * Used only for sync signaling and temporary tokens, never for durable data.
 * Handles SSR safety and error suppression.
 */

/**
 * Safely get an item from localStorage, returning null if SSR or on error.
 */
export function safeGetItem(key: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`safeGetItem(${key}): failed to read from localStorage`, e);
    }
    return null;
  }
}

/**
 * Safely set an item in localStorage, silently failing on error.
 */
export function safeSetItem(key: string, value: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`safeSetItem(${key}): failed to write to localStorage`, e);
    }
  }
}

/**
 * Safely remove an item from localStorage, silently failing on error.
 */
export function safeRemoveItem(key: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`safeRemoveItem(${key}): failed to remove from localStorage`, e);
    }
  }
}

/**
 * Clear all items from localStorage, silently failing on error.
 */
export function safeClearAll(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.clear();
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('safeClearAll: failed to clear localStorage', e);
    }
  }
}
